import React, { Suspense } from "react";
import { useSelector } from "react-redux";
import { captureException } from "@sentry/react";

export function ViewLoading() {
  const arabic = useSelector((state) => state.store?.language === "arabic");
  return <div role="status" aria-live="polite" style={{ minHeight: 160, display: "grid", placeItems: "center", padding: 24, color: "#526078", textAlign: "center" }}>
    {arabic ? "جارٍ التحميل…" : "Loading…"}
  </div>;
}

class LoadBoundary extends React.Component {
  state = { error: false };
  static getDerivedStateFromError() { return { error: true }; }
  componentDidCatch(error) { captureException(error); }
  render() {
    if (!this.state.error) return this.props.children;
    return <LoadFailure onRetry={this.props.onRetry} />;
  }
}

function LoadFailure({ onRetry }) {
  const arabic = useSelector((state) => state.store?.language === "arabic");
  return <div role="alert" dir={arabic ? "rtl" : "ltr"} style={{ padding: 24, textAlign: "center" }}>
    <p>{arabic ? "تعذر تحميل هذا القسم. يرجى المحاولة مرة أخرى." : "This section couldn’t load. Please try again."}</p>
    <button type="button" onClick={onRetry} style={{ minHeight: 44, padding: "8px 20px", borderRadius: 12 }}>
      {arabic ? "إعادة المحاولة" : "Try again"}
    </button>
  </div>;
}

// One module download per view, with a local boundary: a slow/failed optional
// chunk must not blank the storefront shell or reload a customer's cart.
export default function lazyView(load, Loading = ViewLoading) {
  const InitialView = React.lazy(load);
  return function DeferredView(props) {
    const [attempt, setAttempt] = React.useState(0);
    const [View, setView] = React.useState(() => InitialView);
    const retry = () => {
      setView(() => React.lazy(load));
      setAttempt((value) => value + 1);
    };
    return <LoadBoundary key={attempt} onRetry={retry}>
      <Suspense fallback={<Loading {...props} />}><View {...props} /></Suspense>
    </LoadBoundary>;
  };
}
